import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import 'quill-mention';

@Component({
  selector: 'app-quick-comment',
  templateUrl: './quick-add.component.html',
  styleUrls: ['./quick-add.component.scss'],
})
export class QuickCommentComponent implements OnInit, OnDestroy {
  @Input() url: string = '';
  @Input() guid: string = '';
  @Input() title: string = 'Add Comment';
  // When true, this comment is on a secure email so client-visibility is actually toggleable (see
  // comments.component.ts's identical @Input) - off by default since most comment contexts are
  // internal-only.
  @Input() visible_to_client = false;
  // Replying to a comment ({ guid, name, text }) or editing one (the comment itself); neither is a new comment
  @Input() reply_to: any = null;
  @Input() edit_comment: any = null;
  // A saved draft to start from, and whether a new comment can be saved as a draft (the comments list works that out)
  @Input() initial_comment = '';
  @Input() initial_visible_to_client: boolean | undefined = undefined;
  @Input() allow_draft = false;
  @ViewChild('quickCommentEditor') quickCommentEditor: any;

  comment = '';
  saving = false;
  current_user: any = null;
  // Only ever honored by the backend for email_comments (see Comments.CreateComment) - set from the
  // visible_to_client @Input in ngOnInit so it starts matching what will actually happen server-side.
  commentVisibleToClient = false;

  // Which comment routes support the "who has access" endpoint (Comments.GetTeamMembersAccess) -
  // files/comments is a separate, older backend implementation that doesn't have it.
  private static readonly MENTION_ACCESS_SUPPORTED_URLS = [
    'projects/comments', 'tasks/comments', 'steps/comments',
    'client/comments', 'companies/comments', 'emails/comments'
  ];

  // Plural noun for the "not visible to clients" tooltip on every non-email comment type - mirrors
  // comments.component.ts's identical map.
  private static readonly OBJECT_TYPE_LABELS: { [url: string]: string } = {
    'projects/comments': 'Projects',
    'tasks/comments': 'Tasks',
    'steps/comments': 'Actions',
    'client/comments': 'Contacts',
    'companies/comments': 'Companies',
    'files/comments': 'Files'
  };

  // null = access not restricted/not loaded yet (everyone shown as accessible)
  mentionAccess: { guid: string, has_access: boolean }[] | null = null;

  quillModules = {
    mention: {
      allowedChars: /^[A-Za-z0-9_.'\-\s]*$/,
      mentionDenotationChars: ['@'],
      positioningStrategy: 'fixed',
      // Deliberately NOT including 'disabled' here: quill-mention's own selectItem() does
      // `if (data.disabled)` against the DOM dataset, where every value is a string - so
      // disabled:false round-trips as the truthy string "false" and blocks EVERY item, not just
      // disabled ones. Leaving it out of dataAttributes keeps that dataset value unset (falsy),
      // so real clicks work; onSelect below is the actual access gate instead.
      dataAttributes: ['id', 'value', 'denotationChar'],
      source: async (searchTerm: string, renderList: (matches: any[], term: string) => void) => {
        const teamMembers = await this.globalService.GetTeamMembersList() || [];
        const search = (searchTerm || '').toLowerCase();
        const matches = teamMembers
          .filter((member: any) => (member.display_name || '').toLowerCase().includes(search))
          .map((member: any) => ({ id: member.guid, value: member.display_name, disabled: !this.HasMentionAccess(member.guid) }));
        renderList(matches, searchTerm);
      },
      renderItem: (item: any) => {
        const span = document.createElement('span');
        span.textContent = item.value;
        // Fills the whole <li> row (position:relative on .ql-mention-list-item in global.scss) so
        // the entire row is clickable, not just the tight text box - see global.scss for why.
        span.style.display = 'inline-block';
        span.style.padding = '0 20px';
        if (item.disabled) {
          span.title = 'This team member does not have access to this item';
          span.style.opacity = '0.5';
          span.style.cursor = 'not-allowed';
        } else {
          span.addEventListener('mousedown', (event: MouseEvent) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            const quill = this.quickCommentEditor?.quillEditor;
            quill?.getModule('mention')?.insertItem({ id: item.id, value: item.value, denotationChar: item.denotationChar || '@' }, false);
          });
        }
        return span;
      },
    },
  };

  constructor(
    private modalController: ModalController,
    private apiService: ApiService,
    public globalService: GlobalService
  ) {}

  ngOnDestroy() {
    // Otherwise closing a modal mid-search leaves the mention dropdown's floating DOM node
    // (appended straight to <body> by quill-mention for `positioningStrategy: 'fixed'`) behind.
    this.quickCommentEditor?.quillEditor?.getModule('mention')?.hideMentionList();
  }

  OnEditorCreated(quill: any) {
    const mentionModule = quill.getModule('mention');
    if (!mentionModule) return;
    // quill-mention's own Enter handling (selectHandler -> selectItem -> onSelect) isn't reliably
    // firing, same story as the click handling above - reading the highlighted <li> directly and
    // inserting it ourselves sidesteps whatever's wrong with the library's own path. Registered as a
    // real Quill keyboard binding (not a plain addEventListener) and moved to the front of Quill's
    // own bindings list, so it's checked before Quill's default Enter-inserts-a-newline behavior.
    const enterHandler = () => {
      if (!mentionModule.isOpen) return true;
      const selectedLi: HTMLElement = mentionModule.mentionList.querySelector('li.selected');
      if (!selectedLi) return true;
      const item = mentionModule.values?.[Number(selectedLi.dataset.index)];
      if (!item) return true;
      const target = this.HasMentionAccess(item.id) ? item : mentionModule.values?.find((v: any) => this.HasMentionAccess(v.id));
      if (target) mentionModule.insertItem({ ...target, denotationChar: target.denotationChar || '@' }, false);
      return false;
    };
    quill.keyboard.addBinding({ key: 13 }, enterHandler);
    const bindings = quill.keyboard.bindings[13];
    if (bindings && bindings.length > 1) bindings.unshift(bindings.pop());
  }

  async ngOnInit() {
    this.current_user = this.globalService.GetCurrentUser();
    this.commentVisibleToClient = this.visible_to_client;
    if (this.edit_comment) {
      this.comment = this.edit_comment.comment || '';
      if (this.visible_to_client && typeof this.edit_comment.visible_to_client === 'boolean') this.commentVisibleToClient = this.edit_comment.visible_to_client;
    } else {
      if (this.initial_comment) this.comment = this.initial_comment;
      if (this.visible_to_client && typeof this.initial_visible_to_client === 'boolean') this.commentVisibleToClient = this.initial_visible_to_client;
    }
    if (QuickCommentComponent.MENTION_ACCESS_SUPPORTED_URLS.includes(this.url) && this.guid) {
      const data: any = await this.apiService.get(`${this.url}/${this.guid}/team_members`);
      if (data?.success) this.mentionAccess = data.data;
    }
  }

  // Tooltip for the inline "visible to client" indicator next to the mention picker - mirrors
  // comments.component.ts's identical method.
  GetVisibleToClientTooltip(): string {
    if (this.visible_to_client) {
      return this.commentVisibleToClient ? 'This comment will be visible to the client.' : 'This comment will be internal only.';
    }
    const label = QuickCommentComponent.OBJECT_TYPE_LABELS[this.url] || 'Items';
    return `Comments on ${label} are not visible to clients.`;
  }

  HasMentionAccess(guid: string): boolean {
    if (!this.mentionAccess) return true;
    const entry = this.mentionAccess.find(a => a.guid === guid);
    return entry ? entry.has_access : true;
  }

  InsertMention(member: { guid: string, display_name: string }) {
    if (!this.HasMentionAccess(member.guid)) return;
    const quill = this.quickCommentEditor?.quillEditor;
    if (!quill) return;
    // quill-mention's programmatic insert relies on its own tracked cursor position, which is only
    // ever set by its selection-change listener - if the editor was never focused (e.g. picking a
    // team member from the toolbar before ever clicking into the editor), that's still null.
    quill.focus();
    quill.getModule('mention').insertItem({ id: member.guid, value: member.display_name, denotationChar: '@' }, true);
  }

  get submit_label(): string {
    if (this.edit_comment) return 'Update';
    return this.reply_to ? 'Reply' : 'Submit';
  }

  async Submit() {
    if (this.comment == '' || this.saving) return;
    this.saving = true;
    let data: any;
    if (this.edit_comment) {
      data = await this.apiService.put(`${this.url}/${this.edit_comment.guid}`, { ...this.edit_comment, comment: this.comment, visible_to_client: this.commentVisibleToClient });
    } else {
      const body: any = { comment: this.comment, visible_to_client: this.commentVisibleToClient };
      if (this.reply_to) body.reply_to = this.reply_to.guid;
      data = await this.apiService.post(`${this.url}/${this.guid}`, body);
    }
    this.saving = false;
    this.globalService.presentToastMessage(data.message, data.success);
    if (data.success) this.modalController.dismiss({ success: true, data: data.data, comment: this.comment });
  }

  // Keeps what has been typed so far for the next time a new comment is started on this item
  async SaveDraft() {
    if (!this.url || !this.guid || this.comment == '') return;
    const payload = { type: this.url, guid: this.guid, data: JSON.stringify({ comment: this.comment, visible_to_client: this.commentVisibleToClient }) };
    const data: any = await this.apiService.post('comment_drafts', payload);
    this.globalService.presentToastMessage(data.message, data.success);
    if (data.success) this.modalController.dismiss({ draft_saved: true, comment: this.comment });
  }

  Close() {
    this.modalController.dismiss(null);
  }
}
